/**
 * Render Markdown text to HTML string.
 * Supports: headers, bold/italic, inline code, links, lists (ul/ol),
 * blockquotes, horizontal rules, code blocks.
 */
export function renderMarkdown(md: string): string {
  if (!md) return '';

  const lines = md.split('\n');
  const out: string[] = [];
  let inUl = false;
  let inOl = false;
  let inBlockquote = false;
  let inPre = false;
  let buffer: string[] = [];

  const flushList = () => {
    if (inUl) {
      out.push('</ul>');
      inUl = false;
    }
    if (inOl) {
      out.push('</ol>');
      inOl = false;
    }
  };
  const flushBlockquote = () => {
    if (inBlockquote) {
      out.push('</blockquote>');
      inBlockquote = false;
    }
  };
  const flushPara = () => {
    if (buffer.length > 0) {
      const text = buffer.join(' ').trim();
      if (text) {
        out.push(
          `<p class="text-gray-700 text-sm leading-relaxed mb-3">${inlineMarkdown(text)}</p>`
        );
      }
      buffer = [];
    }
  };

  const inlineMarkdown = (text: string): string => {
    return (
      text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-[#1a3a5c]">$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code class="bg-gray-100 text-[#c9a96e] px-1.5 py-0.5 rounded text-xs font-mono">$1</code>')
        .replace(/!\[(.+?)\]\((.+?)\)/g, '<img src="$2" alt="$1" title="$1" class="rounded-lg max-w-full my-4" />')
        .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[#c9a96e] hover:underline">$1</a>')
    );
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      flushPara();
      flushList();
      flushBlockquote();
      if (inPre) {
        out.push('</pre></div>');
        inPre = false;
      } else {
        out.push(
          '<div class="bg-gray-50 rounded-lg p-4 my-3 overflow-x-auto"><pre class="text-xs text-gray-600 font-mono">'
        );
        inPre = true;
      }
      continue;
    }

    if (inPre) {
      out.push(line);
      continue;
    }

    if (trimmed === '---') {
      flushPara();
      flushList();
      flushBlockquote();
      out.push('<hr class="my-6 border-gray-200">');
      continue;
    }

    if (trimmed.startsWith('# ')) {
      flushPara();
      flushList();
      flushBlockquote();
      out.push(
        `<h1 class="text-2xl font-bold text-[#1a3a5c] mt-6 mb-3">${inlineMarkdown(trimmed.slice(2))}</h1>`
      );
      continue;
    }

    if (trimmed.startsWith('## ')) {
      flushPara();
      flushList();
      flushBlockquote();
      out.push(
        `<h2 class="text-xl font-bold text-[#1a3a5c] mt-6 mb-3 pb-1 border-b border-gray-100">${inlineMarkdown(trimmed.slice(3))}</h2>`
      );
      continue;
    }

    if (trimmed.startsWith('### ')) {
      flushPara();
      flushList();
      flushBlockquote();
      out.push(
        `<h3 class="text-lg font-bold text-[#1a3a5c] mt-5 mb-2">${inlineMarkdown(trimmed.slice(4))}</h3>`
      );
      continue;
    }

    const ulMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (ulMatch) {
      flushPara();
      flushBlockquote();
      if (!inUl) {
        out.push(
          '<ul class="list-disc ml-5 mb-3 space-y-1 text-gray-700 text-sm leading-relaxed">'
        );
        inUl = true;
      }
      inOl = false;
      out.push(`<li>${inlineMarkdown(ulMatch[1])}</li>`);
      continue;
    }

    const olMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (olMatch) {
      flushPara();
      flushBlockquote();
      if (!inOl) {
        out.push(
          '<ol class="list-decimal ml-5 mb-3 space-y-1 text-gray-700 text-sm leading-relaxed">'
        );
        inOl = true;
      }
      inUl = false;
      out.push(`<li>${inlineMarkdown(olMatch[1])}</li>`);
      continue;
    }

    const bqMatch = trimmed.match(/^>\s*(.*)$/);
    if (bqMatch) {
      flushPara();
      flushList();
      if (!inBlockquote) {
        out.push(
          '<blockquote class="border-l-4 border-[#c9a96e] pl-4 py-2 my-3 bg-[#f8f5f0] rounded-r-lg text-gray-600 italic text-sm">'
        );
        inBlockquote = true;
      }
      out.push(`<p class="mb-1">${inlineMarkdown(bqMatch[1])}</p>`);
      continue;
    }

    flushList();
    flushBlockquote();

    if (trimmed === '') {
      flushPara();
    } else {
      buffer.push(trimmed);
    }
  }

  flushPara();
  flushList();
  flushBlockquote();

  return out.join('\n');
}